import { createContext, useContext, useEffect, useState, useRef } from 'react'
import { supabase } from '../supabaseClient.js'
import LoadingScreen from '../components/Common/LoadingScreen'

const AuthContext = createContext({})

export const useAuth = () => {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null)
    const [profile, setProfile] = useState(null)
    const [loading, setLoading] = useState(true)
    const [authError, setAuthError] = useState(null)
    const lastLoadedUserId = useRef(null)

    // 1. LIMPIEZA DE LEGADO (CÓDIGO DE UN SOLO USO)
    useEffect(() => {
        // LIMPIEZA DE EMERGENCIA: Borrar basura vieja de los navegadores
        if (localStorage.getItem('omnia_profile')) {
            console.warn('🧹 Purging legacy profile cache for stability.')
            localStorage.removeItem('omnia_profile')
        }
    }, [])

    // 2. FUNCIÓN checkPermission (Infalible)
    const checkPermission = (module, action = 'view') => {
        if (user?.email === 'alejandro@gallegosasesores.com') {
            return true
        }

        if (!profile) return false

        if (profile.role === 'client') {
            return false
        }

        if (profile.role === 'admin' || profile.role === 'owner') return true

        return profile.permissions?.[module]?.[action] || false
    }

    // 3. CARGA DE PERFIL (Simplificada y Robusta)
    const loadUserProfile = async (userId) => {
        if (!userId) {
            setProfile(null)
            // No cambiamos loading aquí porque se maneja en el flujo principal
            return
        }

        try {
            lastLoadedUserId.current = userId
            // console.log('⚡ Loading Profile from DB for:', userId)

            const metadataRole =
                user?.user_metadata?.role || user?.app_metadata?.role || null

            let { data: profileData, error } = await supabase
                .from('profiles')
                .select('*, organization:organizations(*)')
                .eq('id', userId)
                .maybeSingle()

            if (error) {
                console.warn('⚠️ Error fetching profile from DB:', error.message)
            }

            const isPortalClient =
                metadataRole === 'client' ||
                metadataRole === 'client_enterprise' ||
                metadataRole === 'cliente'

            if (!profileData && isPortalClient) {
                // console.log('🌐 Portal client detected without profile. Using in-memory client profile.')
                profileData = {
                    id: userId,
                    email: user?.email,
                    role: 'client',
                    full_name: user?.user_metadata?.full_name || 'Cliente Portal',
                    permissions: {},
                    organization_id: null
                }
                setProfile(profileData)
                return
            }

            let createdProfile = false
            if (!profileData) {
                console.error('⚠️ Perfil no encontrado. Generando perfil temporal de CLIENTE (Seguro).')
                profileData = {
                    id: userId,
                    email: user?.email,
                    role: 'client', // FORCE SECURE DEFAULT
                    full_name: user?.user_metadata?.full_name || 'Usuario',
                    permissions: {},
                    organization_id: null
                }
                // Do NOT create profile in DB from frontend. Let the backend handle it.
                // createdProfile = true 
            }

            if (!profileData.organization_id) {
                const { data: org } = await supabase
                    .from('organizations')
                    .select('id')
                    .limit(1)
                    .maybeSingle()

                if (org) {
                    profileData.organization_id = org.id

                    // console.log('🔧 Auto-fixing missing organization_id in DB...')
                    await supabase
                        .from('profiles')
                        .update({ organization_id: org.id })
                        .eq('id', userId)
                }
            }

            if (createdProfile) {
                try {
                    // console.log('🆕 Creating missing profile row in DB for user:', userId)
                    await supabase.from('profiles').insert({
                        id: profileData.id,
                        email: profileData.email,
                        role: profileData.role,
                        full_name: profileData.full_name,
                        permissions: profileData.permissions,
                        organization_id: profileData.organization_id
                    })
                } catch (e) {
                    console.warn('⚠️ Error creating fallback profile in DB:', e.message)
                }
            }

            setProfile(profileData)
        } catch (error) {
            console.error('❌ Error crítico en loadUserProfile:', error)
            // En caso de error, no bloqueamos la app
        } finally {
            // SIEMPRE liberamos el loading al terminar de cargar el perfil
            setLoading(false)
        }
    }

    // 4. INICIALIZACIÓN Y LISTENERS
    useEffect(() => {
        let mounted = true

        const initAuth = async () => {
            try {
                // Obtener sesión con manejo de errores
                const {
                    data: { session },
                    error
                } = await supabase.auth.getSession()

                if (error) {
                    throw error
                }

                if (!mounted) return

                if (session?.user) {
                    setUser(session.user)
                    await loadUserProfile(session.user.id)
                } else {
                    setUser(null)
                    setProfile(null)
                    setLoading(false)
                }
            } catch (error) {
                console.error('❌ Auth Init Error:', error)
                // REPARACIÓN DE BUCLE INFINITO:
                // Si falla, aseguramos estado limpio y loading false
                setUser(null)
                setProfile(null)
                setLoading(false)
            }
        }

        initAuth()

        const {
            data: { subscription }
        } = supabase.auth.onAuthStateChange(async (event, session) => {
            if (!mounted) return

            // console.log('🔔 Auth Event:', event)

            if (event === 'SIGNED_OUT') {
                // Limpiar todo y redirigir
                setUser(null)
                setProfile(null)
                setLoading(false)
            } else if (event === 'INITIAL_SESSION') {
                // Manejo inicial normal
                setUser(session?.user)
                await loadUserProfile(session?.user?.id)
                setLoading(false)
            } else if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') {
                // IMPORTANTE: NO poner loading(true) aquí.
                // Solo actualizar el usuario en memoria y refrescar perfil en background.
                setUser(session?.user)
                if (session?.user) {
                    loadUserProfile(session.user.id) // Sin await bloqueante
                }
                // Asegurar que loading sea false por si acaso
                setLoading(false)
            }
        })

        return () => {
            mounted = false
            subscription.unsubscribe()
        }
    }, [])

    const signIn = async (email, password) => {
        setLoading(true)
        setAuthError(null)
        try {
            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password
            })
            if (error) throw error
            return data
        } catch (error) {
            setLoading(false)
            setAuthError(error.message)
            throw error
        }
    }

    const signUp = async (email, password) => {
        const { data, error } = await supabase.auth.signUp({
            email,
            password
        })
        if (error) throw error
        return data
    }

    const signOut = async () => {
        // Solo debe volver a true si el usuario hace Logout explícito
        setLoading(true)
        try {
            await supabase.auth.signOut()
        } catch (error) {
            console.error('Error signing out:', error)
        } finally {
            setUser(null)
            setProfile(null)
            lastLoadedUserId.current = null
            // Limpieza total
            localStorage.clear()
            sessionStorage.clear()
            setLoading(false)
        }
    }

    useEffect(() => {
        if (profile && profile.is_active === false) {
            console.warn('🔒 Usuario suspendido detectado. Cerrando sesión automáticamente...')
            signOut()
        }
    }, [profile && profile.is_active])

    const resetPassword = async (email) => {
        const { data, error } = await supabase.auth.resetPasswordForEmail(email)
        if (error) throw error
        return data
    }

    // UI de Error (Simple)
    if (authError && !loading) {
        // Si hay error pero no estamos cargando, permitimos que la app renderice
    }

    const value = {
        user,
        profile,
        organizationId: profile?.organization_id,
        checkPermission,
        signIn,
        signUp,
        signOut,
        resetPassword,
        loading,
        error: authError,
        refreshProfile: () => user && loadUserProfile(user.id)
    }

    if (loading) {
        return <LoadingScreen />
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
